import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box, Button, Chip, CircularProgress, Link, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { FileDownload as FileDownloadIcon } from '@mui/icons-material';
import { FixedAssetReportService } from '../../services/fixedAssetReport.service';
import { DepreciationScheduleRow } from '../../interfaces/fixedAsset/fixedAssetReport.interface';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { toLocalIsoDate } from '../../utils/dateUtils';
import { downloadCsv } from '../../utils/csv';

const money = (n: number) =>
  (n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// month input value ("YYYY-MM") → first-of-month ISO date ("YYYY-MM-01")
const monthToPeriod = (month: string) => (month ? `${month}-01` : '');
const thisMonth = () => toLocalIsoDate().slice(0, 7);
// Financial year to date defaults to the calendar year start (institution fiscal settings are not exposed here).
const yearStartMonth = () => `${toLocalIsoDate().slice(0, 4)}-01`;
const monthLabel = (iso: string) =>
  new Date(`${iso.slice(0, 7)}-01T00:00:00`).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });

const DepreciationScheduleReport: React.FC = () => {
  const toast = useToast();
  const [fromMonth, setFromMonth] = useState(yearStartMonth());
  const [toMonth, setToMonth] = useState(thisMonth());
  const [rows, setRows] = useState<DepreciationScheduleRow[] | null>(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const institutionId = AuthService.getSelectedInstitution();
    try {
      setLoading(true);
      const data = await FixedAssetReportService.schedule(
        institutionId!, monthToPeriod(fromMonth), monthToPeriod(toMonth));
      setRows(data);
    } catch (e) {
      toast.showApiError(e, 'Failed to load depreciation schedule');
    } finally {
      setLoading(false);
    }
  };

  // Open with data: run once for the default range.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const handleExportCsv = () => {
    if (!rows) return;
    const headers = ['Code', 'Name', 'Period', 'Amount', 'Book value after', 'Posted to'];
    const dataRows = rows.map(r => [
      r.assetCode, r.assetName, monthLabel(r.period), r.amount, r.bookValueAfter, r.postedToPeriod ?? '',
    ]);
    downloadCsv(`depreciation-schedule-${fromMonth}_${toMonth}.csv`, headers, dataRows);
  };

  return (
    <Box sx={{ p: 3 }} data-help-page="fixedAssets.dsr.page">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, flexWrap: 'wrap' }} data-help-section="fixedAssets.dsr.filter">
          <Typography variant="h5">Depreciation Schedule</Typography>
          <TextField
            data-help="fixedAssets.dsr.from"
            label="From"
            type="month"
            value={fromMonth}
            onChange={e => setFromMonth(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
          <TextField
            data-help="fixedAssets.dsr.to"
            label="To"
            type="month"
            value={toMonth}
            onChange={e => setToMonth(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
          <Button data-help="fixedAssets.dsr.run" variant="contained" onClick={load} disabled={loading || !fromMonth || !toMonth}>Run</Button>
        </Box>
        <Button
          data-help="fixedAssets.dsr.export"
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          onClick={handleExportCsv}
          disabled={!rows || loading}
        >
          Export CSV
        </Button>
      </Box>

      {loading ? (
        <CircularProgress />
      ) : rows && (
        <TableContainer component={Paper}>
          <Table size="small" data-help-section="fixedAssets.dsr.table">
            <TableHead>
              <TableRow>
                <TableCell data-help="fixedAssets.dsr.col.code">Code</TableCell>
                <TableCell data-help="fixedAssets.dsr.col.name">Name</TableCell>
                <TableCell data-help="fixedAssets.dsr.col.period">Period</TableCell>
                <TableCell data-help="fixedAssets.dsr.col.amount" align="right">Amount</TableCell>
                <TableCell data-help="fixedAssets.dsr.col.book" align="right">Book value after</TableCell>
                <TableCell data-help="fixedAssets.dsr.col.posted">Posted to</TableCell>
                <TableCell data-help="fixedAssets.dsr.col.je">JE</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No depreciation in this period range.</Typography>
                  </TableCell>
                </TableRow>
              )}
              {rows.map((r, i) => (
                <TableRow key={`${r.fixedAssetId}-${r.period}-${i}`} hover>
                  <TableCell>{r.assetCode}</TableCell>
                  <TableCell>{r.assetName}</TableCell>
                  <TableCell>{monthLabel(r.period)}</TableCell>
                  <TableCell align="right">{money(r.amount)}</TableCell>
                  <TableCell align="right">{money(r.bookValueAfter)}</TableCell>
                  <TableCell>
                    {r.postedToPeriod
                      ? <Chip size="small" color="warning" label={monthLabel(r.postedToPeriod)} />
                      : 'Same month'}
                  </TableCell>
                  <TableCell>
                    {r.journalEntryId
                      ? <Link data-help="fixedAssets.dsr.action.je" component={RouterLink} to={`/accounting/journal-entries/${r.journalEntryId}`}>View JE</Link>
                      : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default DepreciationScheduleReport;
